import type { ReactNode } from 'react'

import type {
  FlightPlayerScoresFragment,
  HoleScoreFragment,
} from '../../../__generated__/hooks'
import type { CurrentPlayerCtx } from '../../../PlayerShell'
import { firstName } from '../helpers'
import HoleRow from './HoleRow'
import TotalRow from './TotalRow'

/**
 * The actual scorecard table — header (HULL · player columns) + body
 * (HoleRow per hole + TotalRow per side + grand total). Layout is
 * vertical (rows = holes, cols = players) so it fits a phone width
 * without horizontal scroll.
 *
 * The variant views (`IndividualView`, `BestBallView`) supply:
 *  - `flight` — which entries become columns (players or team-synth)
 *  - `cellSubLabel` — best-ball only; stamps contributor initials
 *  - `entriesAreTeams` — the columns are TEAMS, not players
 */
const Table = ({
  player,
  flight,
  holes,
  cellSubLabel,
  showStableford,
  entriesAreTeams,
  emphasizeTotal = true,
  sideTotalRow,
}: {
  player: CurrentPlayerCtx
  flight: FlightPlayerScoresFragment[]
  holes: HoleScoreFragment[]
  cellSubLabel?: (entryId: string, holeNumber: number) => string | null
  showStableford?: boolean
  /**
   * The columns are teams. A team is named by its whole name — running the
   * first-name shortener over "Engen / Rud" leaves "Engen", which reads as one
   * player — and a best-ball team has no allowance to put in the eyebrow.
   */
  entriesAreTeams?: boolean
  /**
   * False where the per-entry total is not the result. Best ball's columns
   * are four players and its result is two sides, so the emphasis belongs on
   * the row below rather than on a total nobody is competing with.
   */
  emphasizeTotal?: boolean
  /** Rendered as the last row, inside the table. See `SideTotalRow`. */
  sideTotalRow?: ReactNode
}) => {
  const isFull18 = holes.length >= 18
  const front = isFull18 ? holes.slice(0, 9) : holes
  const back = isFull18 ? holes.slice(9, 18) : []

  return (
    <div
      className="mx-2 rounded-lg shadow-sm overflow-hidden"
      style={{ background: 'var(--cream-card)' }}
    >
      <table
        className="w-full border-collapse"
        style={{ tableLayout: 'fixed' }}
      >
        <colgroup>
          <col style={{ width: 56 }} />
          {flight.map((p) => (
            <col key={p.id} />
          ))}
        </colgroup>
        <thead>
          <tr className="bg-green-900 text-paper">
            <th
              className="text-left px-2 py-2 text-[10px] font-bold"
              style={{ letterSpacing: 'var(--ls-caps)' }}
            >
              HULL
            </th>
            {flight.map((p) => {
              // The eyebrow carries the COURSE HANDICAP, not a "Du" marker.
              // This card is the individual competition, and its allowance is
              // the one number the player could not see anywhere: the score
              // entry card shows the match differential in a match event, so
              // without this the stroke-play allowance had no home (POL-79).
              // "Du" was spent space — you know which column is yours; the
              // green tint and left border already say so.
              //
              // "Ditt lag" stays: a team entry's allowance is the team's, and
              // which column is your team is genuinely worth marking.
              const isYou = p.id === player.id
              const isYourTeam = !isYou && p.id === player.team?.id
              // Best ball gives the SIDE no allowance — every player plays
              // their own ball off their own. A number here would be one
              // member's, presented as the team's.
              // Per COLUMN, not per table: a split round's card carries team
              // columns for its one-ball block beside player columns for the
              // rest. The entry says which it is; formats whose whole flight
              // is one kind do not set it and fall back to the table's answer.
              const isTeamColumn = p.isTeamEntry ?? entriesAreTeams
              const teamWithoutAllowance = isTeamColumn && p.strokes == null
              return (
                <th
                  key={p.id}
                  className="px-1 py-2 text-[11px] font-semibold text-center"
                >
                  <span className="block truncate">
                    {isTeamColumn ? p.name : firstName(p.name)}
                  </span>
                  {isYourTeam ? (
                    <span className="block text-[8px] uppercase tracking-wider text-green-100 mt-0.5">
                      Ditt lag
                    </span>
                  ) : (
                    <span className="block text-[8px] uppercase tracking-wider text-green-100 mt-0.5 font-mono num">
                      {/* `strokes` is the allotted strokes, not the handicap
                          index — labelling it "hcp 12" claimed a 12 handicap
                          for a player whose index might be 13,3. */}
                      {teamWithoutAllowance || p.strokes == null
                        ? '—'
                        : p.strokes > 0
                        ? `${p.strokes} slag`
                        : 'scratch'}
                    </span>
                  )}
                </th>
              )
            })}
          </tr>
        </thead>
        <tbody>
          {front.map((h) => (
            <HoleRow
              key={h.hole.number}
              hole={h}
              flight={flight}
              cellSubLabel={cellSubLabel}
              showStableford={showStableford}
            />
          ))}
          {isFull18 ? (
            <TotalRow
              label="Ut"
              flight={flight}
              holes={front}
              showStableford={showStableford}
            />
          ) : null}
          {back.map((h) => (
            <HoleRow
              key={h.hole.number}
              hole={h}
              flight={flight}
              cellSubLabel={cellSubLabel}
              showStableford={showStableford}
            />
          ))}
          {isFull18 ? (
            <TotalRow
              label="Inn"
              flight={flight}
              holes={back}
              showStableford={showStableford}
            />
          ) : null}
          <TotalRow
            label="Totalt"
            flight={flight}
            holes={holes}
            emphasized={emphasizeTotal}
            showStableford={showStableford}
          />
          {sideTotalRow}
        </tbody>
      </table>
    </div>
  )
}

export default Table
